import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { TextInput, Modal } from '@carbon/react';
import MultiStudentSelector from '../../../Components/multiStudentSelector';

const GET_GROUP_BY_ID = gql`
  query GroupById($id: String!) {
    groupById(id: $id) {
      __typename
      id
      name
      ... on Group {
        students { id }
      }
    }
  }
`;

const EDIT_GROUP = gql`
  mutation EditGroup($id: String!, $name: String, $studentIds: [String!]!) {
    editGroup(id: $id, name: $name, studentIds: $studentIds) {
      id
      name
    }
  }
`;

const DELETE_GROUP = gql`
  mutation DeleteGroup($id: String!) {
    deleteGroup(id: $id) {
      id
      name
    }
  }
`;

const GET_ALL_GROUPS = gql`
  query GetAllGroups {
    allGroups { id name }
  }
`;

export default function EditGroup() {
  const navigate = useNavigate();
  // accept either /:id or /:uid route param — some routes use `id`, others `uid`
  const params = useParams();
  const groupId = params.uid ?? params.id;

  // don't run the query if we don't have a valid id to avoid sending null for a NonNull variable
  const { data, loading, error } = useQuery(GET_GROUP_BY_ID, { variables: { id: groupId }, fetchPolicy: 'cache-and-network', skip: !groupId });

  const [groupName, setGroupName] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [continueEnabled, setContinueEnabled] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [editGroup, { loading: editing }] = useMutation(EDIT_GROUP, { refetchQueries: [{ query: GET_ALL_GROUPS }], awaitRefetchQueries: true, errorPolicy: 'all' });
  const [deleteGroup, { loading: deleting }] = useMutation(DELETE_GROUP, { refetchQueries: [{ query: GET_ALL_GROUPS }], awaitRefetchQueries: true, errorPolicy: 'all' });

  useEffect(() => {
    if (data && data.groupById) {
      setGroupName(data.groupById.name || '');
      const students = data.groupById.students ? data.groupById.students.map(s => s.id) : [];
      setSelectedStudents(students);
    }
  }, [data]);

  useEffect(() => {
    let err = '';
    if (!groupName.trim()) err = 'Gruppenname ist erforderlich.';
    setErrorMessage(err);
    setContinueEnabled(!err);
  }, [groupName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!continueEnabled) return;
    try {
      const res = await editGroup({ variables: { id: groupId, name: groupName.trim(), studentIds: selectedStudents } });

      if (res?.errors && res.errors.length > 0) {
        setErrorMessage(res.errors.map((er) => er.message).join('; '));
        return;
      }

      if (!res?.data || !res.data.editGroup) {
        setErrorMessage('Fehler beim Aktualisieren der Gruppe. Keine Antwort vom Server.');
        return;
      }

      navigate('/admin/groups');
    } catch (err) {
      if (err?.graphQLErrors && err.graphQLErrors.length) {
        setErrorMessage(err.graphQLErrors.map((e) => e.message).join('; '));
      } else if (err?.networkError) {
        setErrorMessage('Netzwerkfehler: ' + (err.networkError.message || err.message));
      } else {
        setErrorMessage(err.message || 'Unbekannter Fehler');
      }
      console.error('Edit group error', err);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteGroup({ variables: { id: groupId } });

      if (res?.errors && res.errors.length > 0) {
        setErrorMessage(res.errors.map((er) => er.message).join('; '));
        return;
      }

      if (!res?.data || !res.data.deleteGroup) {
        setErrorMessage('Fehler beim Löschen der Gruppe. Keine Antwort vom Server.');
        return;
      }

      navigate('/admin/groups');
    } catch (err) {
      if (err?.graphQLErrors && err.graphQLErrors.length) {
        setErrorMessage(err.graphQLErrors.map((e) => e.message).join('; '));
      } else if (err?.networkError) {
        setErrorMessage('Netzwerkfehler: ' + (err.networkError.message || err.message));
      } else {
        setErrorMessage(err.message || 'Unbekannter Fehler');
      }
      console.error('Delete group error', err);
    }
  };

  if (loading) return <div>Lädt Gruppendaten...</div>;
  if (error) return (
    <div className="py-4">
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Gruppe nicht gefunden</h2>
          <p className="text-red-700 mb-4">Die angeforderte Gruppe existiert nicht oder konnte nicht geladen werden.</p>
          <button onClick={() => navigate('/admin/groups')} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
            Zurück zur Gruppenverwaltung
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="py-4">
      <div className="max-w-2xl">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Gruppe bearbeiten</h1>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className={`text-sm font-semibold bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={deleting}
          >
            Gruppe löschen
          </button>
        </div>

        <Modal
          open={isDeleteModalOpen}
          danger
          modalHeading="Gruppe wirklich löschen?"
          primaryButtonText="Löschen"
          secondaryButtonText="Abbrechen"
          onRequestClose={() => setIsDeleteModalOpen(false)}
          onRequestSubmit={handleDelete}
          primaryButtonDisabled={deleting}
        >
          <p>Wollen Sie die Gruppe <strong>{groupName}</strong> wirklich löschen? Dies kann nicht rückgängig gemacht werden.</p>
        </Modal>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <TextInput
            id="groupName"
            labelText="Gruppenname"
            placeholder="z.B. BKUS"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            required
          />

          <MultiStudentSelector selectedStudents={selectedStudents} onChange={setSelectedStudents} />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
            <p className="text-sm text-red-600 h-5">{errorMessage}</p>
            <button
              type="submit"
              className={`mainBtn w-full sm:w-auto text-white px-6 py-2 transition-opacity duration-200 ${continueEnabled ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'}`}
              disabled={!continueEnabled || editing}
            >
              Gruppe aktualisieren
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
